import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

// Contextos
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';

// API
import { useParticipanteService } from '../../services/participanteServices';

// Componentes
import CountryPhoneSelect from '../../components/CountryPhoneSelect/CountryPhoneSelect';

// Utils
import {
  formatLimitedPhoneForCountry,
  getDefaultPhoneCountry,
  getPhoneInputMaxLength,
  isPossiblePhoneForCountry,
} from '../../utils/phoneCountries';

// Estilos
import styles from './loginparticipantepage.module.css';

const DEBUG = false;
const dbg = (...args) => { if (DEBUG) console.log('[LoginParticipantePage]', ...args); };

/**
 * Extrai "from" (rota original protegida) + identifica se veio de /res/:idTurma ou /ead/:idTurma
 * - dest: 'res' | 'ead' | ''
 * - turmaHash: string | null
 */
function parseFrom(location) {
  const from = location?.state?.from || null;
  const pathname = from?.pathname || '';
  const search = from?.search || '';
  const hash = from?.hash || '';

  // aceita /res/:idTurma, /responder/:idTurma e /ead/:idTurma
  const match = pathname.match(/^\/(res(?:ponder)?|ead)\/([^/?#]+)/i);
  const rawDest = match?.[1] || null;
  const turmaHash = match?.[2] || null;

  const dest = rawDest
    ? (rawDest.toLowerCase().startsWith('ead') ? 'ead' : 'res')
    : '';

  return { from, pathname, search, hash, turmaHash, dest };
}

function LoginParticipantePage() {
  const { isAuthenticated } = useAuth();
  const { obterParticipantePorCelular } = useParticipanteService();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const [selectedCountry, setSelectedCountry] = useState(() => getDefaultPhoneCountry());
  const [telefone, setTelefone] = useState('');
  const [carregando, setCarregando] = useState(false);
  const telefoneMaxLength = useMemo(
    () => getPhoneInputMaxLength(selectedCountry),
    [selectedCountry]
  );

  const telefoneInputRef = useRef(null);

  const fromInfo = useMemo(() => parseFrom(location), [location]);
  const queryInfo = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    const turma = (params.get('turma') || '').trim();
    const queryDest = (params.get('dest') || '').trim().toLowerCase();

    return {
      turma,
      dest: (queryDest === 'res' || queryDest === 'ead') ? queryDest : '',
    };
  }, [location.search]);
  const turmaHash = queryInfo.turma || fromInfo.turmaHash || '';
  const dest = queryInfo.dest || fromInfo.dest || '';

  // 📱 Máscara telefone
  const handleTelefoneChange = (e) => {
    const valor = e.target.value;
    setTelefone(formatLimitedPhoneForCountry(valor, selectedCountry));
  };

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    setTelefone((prev) => formatLimitedPhoneForCountry(prev, country));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCarregando(true);

    const telefoneLimpo = telefone.replace(/\D/g, '');

    if (!isPossiblePhoneForCountry(telefoneLimpo, selectedCountry)) {
      showToast('O telefone esta incompleto para o pais selecionado.', 3000, 'error');
      setCarregando(false);
      return;
    }

    dbg('fromInfo:', fromInfo);

    const telefoneComDDI = `${selectedCountry.ddi}${telefoneLimpo}`;

    try {
      const res = await obterParticipantePorCelular(telefoneComDDI);

      if (res?.success && res?.data) {
        // Participante existe -> ir para pagina de senha com cel + turma + dest na URL
        const search = new URLSearchParams({
          cel: telefoneLimpo,
          ddi: selectedCountry.ddi,
          turma: turmaHash || '',
          dest: dest || '',
          nome: res.data?.nome || '',
        }).toString();

        navigate(`/participante/senha?${search}`, {
          replace: true,
          state: {
            from: location.state?.from || null,
            participanteNome: res.data?.nome || '',
          }, // preserva rota original
        });
      } else {
        navigate(`/participante/cadastro${cadastroSearch}`, {
          replace: true,
          state: {
            from: location.state?.from || null,
            participanteNaoEncontrado: true,
          },
        });
      }
    } catch (err) {
      dbg('obterParticipantePorCelular erro:', err);
      showToast('Falha ao verificar o celular. Tente novamente.', 3000, 'error');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    dbg('mount -> location:', location);
    dbg('mount -> parseFrom:', fromInfo);
  }, [location, fromInfo]);

  // Se já autenticado, volta pra rota original (state.from) ou fallback com turma+dest ou /participante/page
  useEffect(() => {
    if (isAuthenticated) {
      const fallback =
        (turmaHash && (dest === 'res' || dest === 'ead')) ? `/${dest}/${turmaHash}` : '/participante/page';

      const caminho = location.state?.from?.pathname || fallback;

      dbg('isAuthenticated -> redirecionando para:', caminho);
      navigate(caminho, { replace: true });
    }
  }, [isAuthenticated, location.state, navigate, turmaHash, dest]);

  useEffect(() => {
    telefoneInputRef.current?.focus();
  }, []);

  // URL do cadastro preservando turma/dest
  const cadastroSearch = useMemo(() => {
    const params = new URLSearchParams();
    if (turmaHash) params.set('turma', turmaHash);
    if (dest) params.set('dest', dest);
    const s = params.toString();
    return s ? `?${s}` : '';
  }, [turmaHash, dest]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>Login</h2>

      {DEBUG && (
        <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
          <div><strong>from.pathname:</strong> {fromInfo.pathname || '(vazio)'} </div>
          <div><strong>turmaHash:</strong> {turmaHash || '(não encontrado)'}</div>
          <div><strong>dest:</strong> {dest || '(não encontrado)'}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className={styles.form}>
        <div>
          <p>Selecione o país e digite seu número para localizar seu cadastro.</p>
        </div>
        <div className={styles.phoneRow}>
          <CountryPhoneSelect
            value={selectedCountry}
            onChange={handleCountryChange}
            label="Pais"
            disabled={carregando}
          />

          <input
            type="text"
            ref={telefoneInputRef}
            placeholder={selectedCountry.placeholder}
            value={telefone}
            onChange={handleTelefoneChange}
            className={styles.input}
            required
            inputMode="tel"
            maxLength={telefoneMaxLength}
          />
        </div>

        <button type="submit" className={styles.button} disabled={carregando}>
          {carregando ? 'Verificando...' : 'Continuar'}
        </button>

        <p className={styles.linkText}>
          Ainda não possui acesso?{' '}
          <Link
            to={`/participante/cadastro${cadastroSearch}`}
            state={{ from: location.state?.from || null }}   // ✅ React Router v6
            className={styles.link}
          >
            CADASTRE-SE
          </Link>
        </p>
      </form>
    </div>
  );
}

export default LoginParticipantePage;
